import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components";
import { strings } from "../utils";
import { getAllJobs, deleteJob, editJob } from "../../src/utils/methods.js";
import moment from "moment";
import ModalScreen from "../components/modal.jsx";
import MenuListComposition from "../components/popUpMenu.jsx";
import DeleteIcon from "../components/Icons/deleteIcon.jsx";
import { CircularProgress } from "@mui/material";

const Jobs = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState("");
  const [pausedJobs, setPausedJobs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const jobData = await getAllJobs();
        const sortedJobs = [...jobData].sort((a, b) => {
          const order = { active: 1, paused: 2, closed: 3 };

          const isAClosed = a.closed ? 1 : 0;
          const isBClosed = b.closed ? 1 : 0;

          if (isAClosed !== isBClosed) {
            return isAClosed - isBClosed;
          }

          const statusA = order[a.status?.toLowerCase()] || 4;
          const statusB = order[b.status?.toLowerCase()] || 4;

          return statusA - statusB;
        });

        setJobs(sortedJobs);

        console.log("first", jobs);
      } catch (error) {
        console.error("Error fetching Jobs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleAddJob = () => navigate(`/job/edit/new`);
  const handleViewApplicants = (jobId) => navigate(`/job/applicants/${jobId}`);

  const handleClickOpen = (jobId) => {
    setSelectedJobId(jobId);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedJobId(null);
  };

  const handleDeleteJob = async () => {
    try {
      if (selectedJobId) {
        await deleteJob(selectedJobId);
        setJobs((prevJobs) =>
          prevJobs.filter((job) => job.id !== selectedJobId)
        );
      }
      handleClose();
    } catch (error) {
      console.error("Error deleting Job:", error);
    }
  };

  const handleCloseJob = async (jobId) => {
    try {
      const updatedJob = { closed: "closed" };
      await editJob(jobId, updatedJob);
    } catch (error) {
      console.error("Error closing job:", error);
    }
  };

  const handlePauseJob = async (jobId) => {
    const job = jobs.find((job) => job.id === jobId);
    if (job) {
      const newStatus = job.status === "paused" ? "active" : "paused";
      try {
        const updatedJob = { status: newStatus };
        await editJob(jobId, updatedJob);

        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === jobId ? { ...job, status: newStatus } : job
          )
        );

        if (newStatus === "paused") {
          setPausedJobs((prev) => [...prev, jobId]);
        } else {
          setPausedJobs((prev) => prev.filter((id) => id !== jobId));
        }
      } catch (error) {
        console.error("Error pausing/resuming job:", error);
      }
    }
  };

  const getFormattedDate = (timestamp) => {
    if (timestamp?.seconds) {
      return moment(new Date(timestamp.seconds * 1000)).fromNow();
    }
    return moment(timestamp).fromNow();
  };

  return (
    <div className="container px-auto py-6 h-full bg-border overflow-scroll">
      <div className="flex justify-between mx-10 mb-8">
        <p className="text-[32px] font-semibold">{strings["JobPageTitle"]}</p>
        <Button onClick={handleAddJob}>{strings["PostJobsBtn"]}</Button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center ">
          <CircularProgress />
        </div>
      ) : jobs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mx-10">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="border rounded-2xl py-6 px-5 bg-white shadow-md flex flex-col justify-between"
            >
              <div className="flex justify-between">
                <p className="text-sm font-normal text-color-1">
                  {getFormattedDate(job.lastUpdated)}
                </p>
                <div>
                  <MenuListComposition
                    jobId={job.id}
                    onEdit={() => navigate(`/job/edit/${job.id}`)}
                    onDelete={() => handleClickOpen(job.id)}
                    onClose={() => handleCloseJob(job.id)}
                    onPause={() => handlePauseJob(job.id)}
                  />
                </div>
              </div>
              <div className="pb-8">
                <p className="flex">
                  <p className="text-2xl font-semibold pt-[16px] pb-3">
                    {job.title}
                  </p>

                  <p
                    className={`text-base font-semibold h-fit px-4 rounded-full mt-5 ml-3 ${
                      job.closed
                        ? "bg-color-12 text-white"
                        : job.status === "active"
                        ? "bg-color-11 text-white"
                        : "bg-color-14 text-white"
                    }`}
                  >
                    {job.closed?.charAt(0).toUpperCase() +
                      job.closed?.slice(1) ||
                      job.status?.charAt(0).toUpperCase() +
                        job.status?.slice(1)}
                  </p>
                </p>
                <div className="flex space-x-3">
                  <p className="p-3 rounded-md bg-border text-sm font-normal text-color-1">
                    {job.industry}
                  </p>
                  <p className="p-3 rounded-md bg-border text-sm font-normal text-color-1">
                    {job.role}
                  </p>
                  <p className="p-3 rounded-md bg-border text-sm font-normal text-color-1">
                    {job.jobsType}
                  </p>
                </div>
              </div>
              <div>
                <Button
                  classes="!w-full justify-center border-primary text-base text-primary font-semibold rounded-md hover:bg-primary hover:text-white"
                  variant="outlineArrow"
                  onClick={() => {
                    navigate(`/job/applicants/${job.id}`, {
                      state: { title: job?.title },
                    });
                  }}
                >
                  {strings["viewApplicantsBtn"]}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex w-full justify-center items-center mt-10">
          <p>No jobs found.</p>
        </div>
      )}

      <ModalScreen
        open={openDialog}
        onClose={handleClose}
        onConfirm={handleDeleteJob}
        title={strings["deleteJobTitle"]}
        description={strings["deleteJobDesc"]}
        confirmButtonText={strings["DeleteBtn"]}
        cancelButtonText={strings["cancelBtn"]}
        hideClass="hidden"
        icon={DeleteIcon}
      />
    </div>
  );
};

export default Jobs;
